import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { useAuth } from "../contexts/AuthContext";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { LockedFeature } from "@/components/shared/LockedFeature";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  Brain, Play, Loader2, TrendingDown, Users, Star,
  XCircle, Clock, MessageSquare, Calendar, Activity,
  CheckCircle, AlertTriangle, Zap, Settings, Save,
  History, Lightbulb, RefreshCw
} from "lucide-react";

// mode: "auto" = executes action automatically; "alert" = notifies owner only
const RULE_META = {
  sales_drop: {
    icon: TrendingDown, color: "text-red-500",
    mode: "alert", channel: "WhatsApp ao dono",
    impactDesc: (d) => d?.drop_pct ? `Queda de ${d.drop_pct}% detetada` : null,
  },
  sleeping_customers: {
    icon: Users, color: "text-amber-500",
    mode: "auto", channel: "WhatsApp aos clientes",
    impactDesc: (d) => d?.sent ? `${d.sent} clientes contactados` : null,
  },
  top_dish: {
    icon: Star, color: "text-emerald-500",
    mode: "auto", channel: "Menu + alerta ao dono",
    impactDesc: (d) => d?.name ? `${d.name} · ${d.count} vendas` : null,
  },
  cancellation_spike: {
    icon: XCircle, color: "text-red-400",
    mode: "alert", channel: "WhatsApp ao dono",
    impactDesc: (d) => d?.rate ? `Taxa: ${d.rate}% (${d.cancelled}/${d.total} pedidos)` : null,
  },
  peak_hour: {
    icon: Clock, color: "text-blue-500",
    mode: "alert", channel: "WhatsApp ao dono",
    impactDesc: (d) => d?.target_hour != null ? `Pico previsto às ${d.target_hour}h` : null,
  },
  feedback_request: {
    icon: MessageSquare, color: "text-purple-500",
    mode: "auto", channel: "WhatsApp ao cliente",
    impactDesc: () => null,
  },
  slow_day: {
    icon: Calendar, color: "text-orange-500",
    mode: "alert", channel: "WhatsApp ao dono",
    impactDesc: (d) => d?.day_name ? `Dia identificado: ${d.day_name}` : null,
  },
};

export default function KaisoBrainPage() {
  const gate = useFeatureGate("kaiso_brain");
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const configInitializedRef = useRef(false);
  const runTimeoutRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("rules");
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/brain/status");
      setStatus(res.data);
      if (res.data.config && !configInitializedRef.current) {
        setConfig(res.data.config);
        configInitializedRef.current = true;
      }
    } catch {
      toast.error("Erro ao carregar status do Brain");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => () => clearTimeout(runTimeoutRef.current), []);

  const runNow = async () => {
    setRunning(true);
    try {
      await authAxiosRef.current.post("/brain/run");
      toast.success("Análise iniciada! Os alertas surgirão no histórico em breve.");
      setHistory(null);
      runTimeoutRef.current = setTimeout(() => {
        fetchStatus();
        setRunning(false);
      }, 4000);
    } catch {
      toast.error("Erro ao iniciar análise. Verifique a ligação.");
      setRunning(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await authAxiosRef.current.put("/brain/config", { rules: config });
      toast.success("Configuração guardada!");
      setShowConfig(false);
    } catch {
      toast.error("Erro ao guardar configuração");
    }
    setSaving(false);
  };

  const loadHistory = useCallback(async (force = false) => {
    if (history !== null && !force) return;
    setHistoryLoading(true);
    try {
      const res = await authAxiosRef.current.get("/brain/notifications?limit=100");
      setHistory(res.data.notifications || []);
    } catch {
      toast.error("Erro ao carregar histórico");
    }
    setHistoryLoading(false);
  }, [history]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "history") loadHistory();
  };

  const toggleRule = (ruleId) => {
    setConfig(prev => ({
      ...prev,
      [ruleId]: { ...prev[ruleId], enabled: !prev[ruleId]?.enabled }
    }));
  };

  const updateRuleParam = (ruleId, key, value) => {
    setConfig(prev => ({
      ...prev,
      [ruleId]: { ...prev[ruleId], [key]: value }
    }));
  };

  if (gate === "blocked") return <MainLayout><LockedFeature featureName="Kaiso Brain" addonId="kaiso_brain" /></MainLayout>;

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#CDA34F]" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div data-testid="brain-page" className="space-y-6 max-w-5xl mx-auto px-4 py-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#CDA34F] to-[#a8832e] flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Kaiso Brain</h1>
              <p className="text-sm text-gray-500">Motor de inteligência proativa · analisa e age automaticamente</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => navigate("/oportunidades")}
              variant="outline"
              size="sm"
              className="gap-1.5"
            >
              <Lightbulb className="w-4 h-4 text-[#CDA34F]" /> Oportunidades
            </Button>
            <Button
              data-testid="brain-config-btn"
              onClick={() => setShowConfig(!showConfig)}
              variant={showConfig ? "default" : "outline"}
              size="sm"
            >
              <Settings className="w-4 h-4 mr-1" /> Configurar
            </Button>
            <Button
              data-testid="brain-run-btn"
              onClick={runNow}
              disabled={running}
              className="bg-[#CDA34F] hover:bg-[#b8923e] text-white"
              size="sm"
            >
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              {running ? "A analisar..." : "Executar Agora"}
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-sm text-gray-500">Estado</p>
                  <p className="text-lg font-semibold text-emerald-600" data-testid="brain-active-status">
                    Ativo · 7 regras
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-[#CDA34F]" />
                <div>
                  <p className="text-sm text-gray-500">Ações executadas</p>
                  <p className="text-lg font-semibold" data-testid="brain-notification-count">
                    {status?.total_notifications || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-500">Última execução</p>
                  <p className="text-sm font-medium" data-testid="brain-last-run">
                    {status?.last_run
                      ? new Date(status.last_run).toLocaleString("pt-PT")
                      : "Ainda não executou"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Config Panel */}
        {showConfig && config && (
          <Card data-testid="brain-config-panel">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Configuração das Regras</CardTitle>
              <Button data-testid="brain-config-save" onClick={saveConfig} disabled={saving} size="sm" className="bg-[#CDA34F] hover:bg-[#b8923e] text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Guardar
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(RULE_META).map(([ruleId, meta]) => {
                const ruleCfg = config[ruleId] || {};
                const Icon = meta.icon;
                const enabled = ruleCfg.enabled !== false;
                return (
                  <div key={ruleId} data-testid={`brain-config-${ruleId}`} className={`p-4 rounded-lg border ${enabled ? "bg-white border-gray-200" : "bg-gray-50 border-gray-100 opacity-60"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${meta.color}`} />
                        <span className="font-medium text-sm">
                          {status?.rules?.find(r => r.id === ruleId)?.name || ruleId}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${meta.mode === "auto" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                          {meta.mode === "auto" ? "Automático" : "Apenas alerta"}
                        </span>
                      </div>
                      <Switch data-testid={`brain-toggle-${ruleId}`} checked={enabled} onCheckedChange={() => toggleRule(ruleId)} />
                    </div>
                    {enabled && (
                      <div className="flex flex-wrap gap-3 mt-2">
                        {ruleId === "sales_drop" && (
                          <label className="text-xs text-gray-500 flex items-center gap-1">
                            Queda mínima:
                            <input type="number" value={ruleCfg.threshold ?? 30} onChange={(e) => updateRuleParam(ruleId, "threshold", parseInt(e.target.value) || 30)} className="w-14 px-1 py-0.5 border rounded text-xs text-center" min={5} max={80} />%
                          </label>
                        )}
                        {ruleId === "sleeping_customers" && (
                          <>
                            <label className="text-xs text-gray-500 flex items-center gap-1">
                              De
                              <input type="number" value={ruleCfg.inactive_days_min ?? 15} onChange={(e) => updateRuleParam(ruleId, "inactive_days_min", parseInt(e.target.value) || 15)} className="w-12 px-1 py-0.5 border rounded text-xs text-center" min={5} max={60} />
                              dias
                            </label>
                            <label className="text-xs text-gray-500 flex items-center gap-1">
                              até
                              <input type="number" value={ruleCfg.inactive_days_max ?? 30} onChange={(e) => updateRuleParam(ruleId, "inactive_days_max", parseInt(e.target.value) || 30)} className="w-12 px-1 py-0.5 border rounded text-xs text-center" min={10} max={90} />
                              dias
                            </label>
                            <label className="text-xs text-gray-500 flex items-center gap-1">
                              Desconto:
                              <input type="number" value={ruleCfg.discount ?? 15} onChange={(e) => updateRuleParam(ruleId, "discount", parseInt(e.target.value) || 15)} className="w-12 px-1 py-0.5 border rounded text-xs text-center" min={5} max={50} />%
                            </label>
                          </>
                        )}
                        {ruleId === "cancellation_spike" && (
                          <label className="text-xs text-gray-500 flex items-center gap-1">
                            Taxa mínima:
                            <input type="number" value={ruleCfg.threshold ?? 10} onChange={(e) => updateRuleParam(ruleId, "threshold", parseInt(e.target.value) || 10)} className="w-14 px-1 py-0.5 border rounded text-xs text-center" min={3} max={50} />%
                          </label>
                        )}
                        {ruleId === "peak_hour" && (
                          <label className="text-xs text-gray-500 flex items-center gap-1">
                            Avisar
                            <input type="number" value={ruleCfg.hours_before ?? 2} onChange={(e) => updateRuleParam(ruleId, "hours_before", parseInt(e.target.value) || 2)} className="w-10 px-1 py-0.5 border rounded text-xs text-center" min={1} max={6} />
                            h antes
                          </label>
                        )}
                        {ruleId === "feedback_request" && (
                          <label className="text-xs text-gray-500 flex items-center gap-1">
                            Enviar
                            <input type="number" value={ruleCfg.hours_after_delivery ?? 2} onChange={(e) => updateRuleParam(ruleId, "hours_after_delivery", parseInt(e.target.value) || 2)} className="w-10 px-1 py-0.5 border rounded text-xs text-center" min={1} max={12} />
                            h após entrega
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex border-b border-border gap-0">
          {[
            { id: "rules", label: "Regras Automáticas", icon: Zap },
            { id: "history", label: "Histórico Completo", icon: History },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-[#CDA34F] text-[#CDA34F]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === "history" && status?.total_notifications > 0 && (
                <Badge variant="secondary" className="text-xs ml-1">{status.total_notifications}</Badge>
              )}
            </button>
          ))}
        </div>

        {/* Tab: Regras Automáticas */}
        {activeTab === "rules" && (
          <Card>
            <CardContent className="pt-4">
              <div className="space-y-2" data-testid="brain-rules-list">
                {status?.rules?.map((rule) => {
                  const meta = RULE_META[rule.id] || {};
                  const Icon = meta.icon || CheckCircle;
                  const count = status?.by_rule?.[rule.id] || 0;
                  const isEnabled = config?.[rule.id]?.enabled !== false;
                  const lastEntry = status?.recent?.find(n => n.rule === rule.id);
                  const impactText = meta.impactDesc && lastEntry?.data ? meta.impactDesc(lastEntry.data) : null;
                  return (
                    <div
                      key={rule.id}
                      data-testid={`brain-rule-${rule.id}`}
                      className={`p-3 rounded-lg border transition-colors ${isEnabled ? "bg-gray-50 border-gray-100 hover:bg-gray-100" : "bg-gray-50 border-gray-100 opacity-50"}`}
                    >
                      <div className="flex items-start gap-3">
                        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${meta.color || "text-gray-500"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm">{rule.name}</p>
                            {meta.mode && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${meta.mode === "auto" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                                {meta.mode === "auto" ? "Automático" : "Apenas alerta"}
                              </span>
                            )}
                            {!isEnabled && (
                              <Badge variant="outline" className="text-xs text-gray-400">Desativada</Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            {meta.channel && (
                              <span className="text-xs text-gray-400">📡 {meta.channel}</span>
                            )}
                            {lastEntry && (
                              <span className="text-xs text-gray-400">
                                Último disparo: {new Date(lastEntry.sent_at).toLocaleDateString("pt-PT")}
                                {impactText ? ` · ${impactText}` : ""}
                              </span>
                            )}
                            {count === 0 && isEnabled && (
                              <span className="text-xs text-gray-400">Ainda não disparou</span>
                            )}
                          </div>
                        </div>
                        <Badge variant={count > 0 ? "default" : "secondary"} className="text-xs shrink-0 mt-0.5">
                          {count}×
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab: Histórico Completo */}
        {activeTab === "history" && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Histórico de Ações
                  {history !== null && (
                    <Badge variant="secondary" className="text-xs">{history.length} registos</Badge>
                  )}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => loadHistory(true)}
                  disabled={historyLoading}
                  className="text-xs gap-1.5 text-muted-foreground"
                >
                  {historyLoading
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <RefreshCw className="w-3.5 h-3.5" />}
                  Atualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-[#CDA34F]" />
                </div>
              ) : history?.length > 0 ? (
                <div className="space-y-2" data-testid="brain-full-history">
                  {history.map((n, i) => {
                    const meta = RULE_META[n.rule] || {};
                    const Icon = meta.icon || AlertTriangle;
                    const color = meta.color || "text-gray-500";
                    const ruleName = status?.rules?.find((r) => r.id === n.rule)?.name || n.rule;
                    const impact = meta.impactDesc && n.data ? meta.impactDesc(n.data) : null;
                    return (
                      <div key={n.id || i} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <p className="font-medium text-sm">{ruleName}</p>
                            <p className="text-xs text-gray-400 shrink-0">
                              {new Date(n.sent_at).toLocaleString("pt-PT")}
                            </p>
                          </div>
                          {impact ? (
                            <p className="text-xs text-gray-600 mt-0.5">📊 {impact}</p>
                          ) : n.data && Object.keys(n.data).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {Object.entries(n.data).map(([k, v]) => (
                                <Badge key={k} variant="outline" className="text-xs">
                                  {k}: {typeof v === "number" ? (Number.isInteger(v) ? v : v.toFixed(1)) : String(v)}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 text-gray-400" data-testid="brain-no-history">
                  <Brain className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Nenhuma ação registada ainda.</p>
                  <p className="text-xs mt-1">O Brain analisa automaticamente a cada hora.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}
